import { ButtonContainer, CurrentDate, FiscalYearPickerContainer, HistoryItem, HistoryList, HistorySection, InfoText, ModalBackdrop, ModalContent, ModalHeader, SectionHeader, ValidationError } from './BomComponent.styles';
import { Control, useForm } from 'react-hook-form';
import { ErrorContainer, ErrorMessage } from '../../../shared/styles/components/feedback.styles';
import React, { useCallback, useState } from 'react';

import { ActionButton } from '../../../shared/styles/components/integrations.styles';
import { BomError } from '../hooks/useBomVersions';
import Button from '@ingka/button';
import FiscalYearPicker from './FiscalYearPicker';
import { formatBackendDateForDisplay } from '../../../shared/utils/fiscal-year.utils';

interface FormValues {
    bomValidFrom: string | null;
}

interface CloneBomModalProps {
    onClose: () => void;
    onSubmit: (data: FormValues) => Promise<void>;
    bomVersionHistory: string[];
    currentBomValidFrom: string | null;
    isCloning: boolean;
    error: BomError | null;
    onRetry: () => void;
}

const CloneBomModal: React.FC<CloneBomModalProps> = ({
    onClose,
    onSubmit,
    bomVersionHistory,
    currentBomValidFrom,
    isCloning,
    error,
    onRetry
}) => {
    const [hasValidationError, setHasValidationError] = useState(false);

    const {
        control,
        getValues,
        formState: { errors }
    } = useForm<FormValues>({
        defaultValues: {
            bomValidFrom: null
        }
    });

    const handleFormSubmit = () => {
        const data = getValues();

        if (!data.bomValidFrom) {
            setHasValidationError(true);
            return;
        }

        setHasValidationError(false);

        try {
            onSubmit(data);
        } catch (err) {
            console.error('Error in form submission:', err);
        }
    };

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
        }
    }, [onClose]);

    const handleModalContentClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
    }, []);

    return (
        <ModalBackdrop onClick={onClose}>
            <ModalContent
                onClick={handleModalContentClick}
                onKeyDown={handleKeyDown}
            >
                <ModalHeader>Clone Bill of Materials</ModalHeader>

                <InfoText>
                    Cloning will create a new version of the Bill of Materials with the selected valid from date.
                </InfoText>

                {error && (
                    <ErrorContainer>
                        <ErrorMessage>{error.message}</ErrorMessage>
                        {error.retryable && (
                            <ActionButton
                                onClick={onRetry}
                                disabled={isCloning}
                            >
                                Retry
                            </ActionButton>
                        )}
                    </ErrorContainer>
                )}

                <FiscalYearPickerContainer>
                    <FiscalYearPicker
                        control={control as Control<FormValues>}
                        name="bomValidFrom"
                        initialValue={null}
                    />
                    {(hasValidationError || errors.bomValidFrom) && (
                        <ValidationError>
                            Please select a valid date
                        </ValidationError>
                    )}
                </FiscalYearPickerContainer>

                {bomVersionHistory.length > 0 && (
                    <HistorySection>
                        <SectionHeader>BOM History</SectionHeader>
                        <HistoryList>
                            {bomVersionHistory.map((date: string) => (
                                <HistoryItem key={date}>
                                    {date === currentBomValidFrom ? (
                                        <CurrentDate>{formatBackendDateForDisplay(date)} (current)</CurrentDate>
                                    ) : (
                                        formatBackendDateForDisplay(date)
                                    )}
                                </HistoryItem>
                            ))}
                        </HistoryList>
                    </HistorySection>
                )}

                <ButtonContainer>
                    <Button
                        text="Cancel"
                        type="secondary"
                        onClick={onClose}
                        disabled={isCloning}
                    />
                    <Button
                        text={isCloning ? "Cloning..." : "Clone"}
                        type="primary"
                        onClick={handleFormSubmit}
                        disabled={isCloning}
                    />
                </ButtonContainer>
            </ModalContent>
        </ModalBackdrop>
    );
};

export default CloneBomModal;
